// ABOUTME: Rich text editor component using Tiptap for WYSIWYG HTML editing
// ABOUTME: Provides toolbar with formatting options and outputs clean HTML

import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Box, IconButton, Divider, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
} from '@mui/icons-material';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const theme = useTheme();
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto;',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        style: 'min-height: 300px; padding: 16px; outline: none;',
      },
    },
  });

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
          title="Bold"
          aria-label="Bold"
        >
          <FormatBold />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
          title="Italic"
          aria-label="Italic"
        >
          <FormatItalic />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleCode().run()}
          color={editor.isActive('code') ? 'primary' : 'default'}
          title="Inline Code"
          aria-label="Inline Code"
        >
          <Code />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
          title="Bullet List"
          aria-label="Bullet List"
        >
          <FormatListBulleted />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
          title="Numbered List"
          aria-label="Numbered List"
        >
          <FormatListNumbered />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          color={editor.isActive('blockquote') ? 'primary' : 'default'}
          title="Quote"
          aria-label="Quote"
        >
          <FormatQuote />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <IconButton size="small" onClick={addLink} title="Add Link" aria-label="Add Link">
          <LinkIcon />
        </IconButton>
        <IconButton size="small" onClick={addImage} title="Add Image" aria-label="Add Image">
          <ImageIcon />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <IconButton
          size="small"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
          aria-label="Undo"
        >
          <Undo />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
          aria-label="Redo"
        >
          <Redo />
        </IconButton>
      </Box>

      {/* Editor Content */}
      <Box
        sx={{
          '& .ProseMirror': {
            minHeight: '300px',
            '& p': { margin: '0.5em 0' },
            '& h1': { fontSize: '2em', fontWeight: 600, margin: '0.67em 0' },
            '& h2': { fontSize: '1.5em', fontWeight: 600, margin: '0.75em 0' },
            '& h3': { fontSize: '1.17em', fontWeight: 600, margin: '0.83em 0' },
            '& ul, & ol': { paddingLeft: '2em', margin: '0.5em 0' },
            '& blockquote': {
              borderLeft: `3px solid ${theme.palette.grey[300]}`,
              paddingLeft: '1em',
              marginLeft: 0,
              fontStyle: 'italic',
              color: 'text.secondary',
            },
            '& code': {
              backgroundColor: 'grey.100',
              padding: '2px 6px',
              borderRadius: `${Number(theme.shape.borderRadius) / 3}px`, // = 4px
              fontFamily: 'monospace',
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '1em 0',
            },
          },
          '& .ProseMirror p.is-editor-empty:first-child::before': {
            content: `"${placeholder || 'Start typing...'}"`,
            color: 'text.disabled',
            pointerEvents: 'none',
            height: 0,
            float: 'left',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Helper Text */}
      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          Tip: To embed a video, type <strong>[video:ID]</strong> where ID is the video number from the Videos tab
        </Typography>
      </Box>
    </Paper>
  );
};

export default RichTextEditor;
